import { useSelector } from 'react-redux';
import { FiMessageSquare, HiHashtag, PiSlackLogo } from '../react-icons/main/index.esm';
import { useEffect, useState, useMemo } from 'react';
import { getMindCloudEmbeddedUserToken } from '@/redux/actions/gravityActions';
import { initializeEmbeddedSDK } from '@/lib/embeddedSdk';
import { action } from '@/lib/api';
import Panel from '../panel/Panel';
import ArgumentWrapper from './ArgumentWrapper';
import { MINDCLOUD_EMBEDDED_SLACK_INTEGRATION_ID } from '@shared/schema/constants';

const getLabel = (value, options) => {
  const option = options.find((el) => el.value === value);
  return option?.label || value;
};

const FlowControlSendSlackMessage = ({ level, handleChange, values = {}, disabled = false }) => {
  const slackInstallation = useSelector((state) => state.workflow?.slackInstallation);
  const slackInstallationChannels = useSelector((state) => state.workflow?.slackInstallationChannels);

  const [loadingChannels, setLoadingChannels] = useState(false);
  const [hasFetchedChannels, setHasFetchedChannels] = useState(false);

  const sendSlackMessage = values?.sendSlackMessage;
  const slackChannel = values?.slackChannel;
  const slackMessageBody = values?.slackMessageBody;

  const label = useMemo(() => {
    if (level === 'failure') {
      return 'Send Slack message on failure?';
    }
    if (level === 'success') {
      return 'Send Slack message on success?';
    }
    return 'Send Slack message on completion?';
  }, [level]);

  useEffect(() => {
    const fetchSlackChannels = async () => {
      if (!!sendSlackMessage && !loadingChannels && slackInstallationChannels?.length === 0 && !hasFetchedChannels) {
        setLoadingChannels(true);
        try {
          const sdkInstance = await initializeEmbeddedSDK();

          const tokenResponse = await getMindCloudEmbeddedUserToken();
          if (!tokenResponse.token) {
            return;
          }
          sdkInstance.setToken(tokenResponse.token);

          const integrations = await sdkInstance.getIntegrations();

          const slackInstallationId = integrations?.find((integration) => integration.id === MINDCLOUD_EMBEDDED_SLACK_INTEGRATION_ID)?.installations?.[0]?.id;
          action('SET_MIND_CLOUD_EMBEDDED_SLACK_INSTALLATION', { data: slackInstallationId });

          const response = await sdkInstance.runAppAction({
            installationId: slackInstallationId,
            actionId: 'p4vt344iajfu',
            appId: 'HHSTHkK9BAPm'
          });

          if (response?.success && response?.data) {
            const channels = response.data
              .filter((channel) => !channel.isArchived)
              .map((channel) => ({
                value: channel.id,
                label: `#${channel.name}`
              }));
            action('SET_MIND_CLOUD_EMBEDDED_SLACK_INSTALLATION_CHANNELS', { data: channels });
          }
        } catch (error) {
          console.error('Error fetching Slack channels:', error);
        } finally {
          setLoadingChannels(false);
          setHasFetchedChannels(true);
        }
      }
    };

    fetchSlackChannels();
  }, [sendSlackMessage, slackInstallationChannels, loadingChannels]);

  return (
    <>
      {slackInstallation && (
        <ArgumentWrapper>
          <Panel.Toggle label={label} icon={PiSlackLogo} active={sendSlackMessage} onChange={(value) => handleChange('sendSlackMessage', value)} disabled={disabled} size="xs" />
        </ArgumentWrapper>
      )}
      {sendSlackMessage && slackInstallation && (
        <>
          <ArgumentWrapper>
            <Panel.Select //
              label="Slack Channel"
              icon={HiHashtag}
              description={'The Slack channel to send the message to'}
              value={loadingChannels ? null : getLabel(slackChannel, slackInstallationChannels)}
              onChange={({ value }) => handleChange('slackChannel', value)}
              options={slackInstallationChannels}
              disabled={disabled || loadingChannels}
              placeholder={loadingChannels ? 'Loading channels...' : 'Select a Slack channel...'}
            />
          </ArgumentWrapper>
          <ArgumentWrapper>
            <Panel.LexicalInput //
              label="Message"
              icon={FiMessageSquare}
              description={'The message to send to the Slack channel'}
              placeholder={'Message...'}
              value={slackMessageBody}
              onChange={(value) => handleChange('slackMessageBody', value)}
              disabled={disabled}
              inputProps={{
                style: { maxHeight: 150 }
              }}
            />
          </ArgumentWrapper>
        </>
      )}
    </>
  );
};

export default FlowControlSendSlackMessage;
